import React, { useMemo, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectAutocompleteProps {
  label: string;
  placeholder?: string;
  options: Option[];
  value: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  groupBy?: (option: Option) => string;
}

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const MultiSelectAutocomplete: React.FC<MultiSelectAutocompleteProps> = React.memo(({
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled = false,
  groupBy,
}) => {
  // Create Select All option
  const selectAllOption: Option = useMemo(() => ({
    value: SELECT_ALL_VALUE,
    label: "Select All",
  }), []);

  // Combine Select All with options
  const allOptions = useMemo(() => {
    return [selectAllOption, ...options];
  }, [selectAllOption, options]);

  const selectedOptions = useMemo(() =>
    options.filter((opt) => value.includes(opt.value)),
    [options, value]
  );

  const isAllSelected = useMemo(() =>
    value.length === options.length && options.length > 0,
    [value.length, options.length]
  );

  const handleChange = useCallback((_event: any, newValue: Option[]) => {
    // Check if Select All was clicked
    const hasSelectAll = newValue.some((opt) => opt.value === SELECT_ALL_VALUE);

    if (hasSelectAll) {
      // If Select All was just added, select all options
      if (!isAllSelected) {
        onChange(options.map((opt) => opt.value));
      } else {
        // If all were already selected, deselect all
        onChange([]);
      }
    } else {
      // Normal selection without Select All
      onChange(newValue.map((opt) => opt.value));
    }
  }, [isAllSelected, onChange, options]);

  return (
    <Autocomplete
      multiple
      options={allOptions}
      value={selectedOptions}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      disabled={disabled}
      disableCloseOnSelect
      limitTags={2}
      groupBy={
        groupBy
          ? (option) =>
              option.value === SELECT_ALL_VALUE ? "" : groupBy(option)
          : undefined
      }
      renderTags={(tagValue, getTagProps) => {
        const numTags = tagValue.length;
        const displayLimit = 2;

        return (
          <>
            {tagValue.slice(0, displayLimit).map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.label}
                  {...chipProps}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: "#e6faf5",
                    border: "1px solid #00d2aa",
                    color: "#0b2b26",
                    fontSize: "13px",
                    fontFamily: "G42 Sans, sans-serif",
                    maxWidth: "150px",
                    height: "28px",
                    margin: "2px",
                    "& .MuiChip-label": {
                      paddingLeft: "10px",
                      paddingRight: "8px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#00d2aa",
                      fontSize: "16px",
                      "&:hover": {
                        color: "#00a888",
                      },
                    },
                  }}
                />
              );
            })}
            {numTags > displayLimit && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: "#5b6b9e",
                  fontSize: "13px",
                  fontFamily: "G42 Sans, sans-serif",
                  fontWeight: 600,
                  padding: "4px 8px",
                }}
              >
                +{numTags - displayLimit} more
              </Box>
            )}
          </>
        );
      }}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;

        // Handle Select All option
        if (option.value === SELECT_ALL_VALUE) {
          return (
            <li key={key} {...otherProps}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={value.length > 0 && !isAllSelected}
                sx={{ mr: 1 }}
              />
              <ListItemText
                primary={option.label}
                sx={{ fontWeight: 600, color: "#5b6b9e" }}
              />
            </li>
          );
        }

        // Handle regular options
        const isSelected = value.includes(option.value);
        return (
          <li key={key} {...otherProps}>
            <Checkbox checked={isSelected} sx={{ mr: 1 }} />
            <ListItemText primary={option.label} />
          </li>
        );
      }}
      renderGroup={
        groupBy
          ? (params) => (
              <li key={params.key}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="primary"
                  >
                    {params.group}
                  </Typography>
                </Box>
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ""}
        />
      )}
      slotProps={{
        popper: {
          sx: {
            maxHeight: "400px",
          },
        },
      }}
      sx={{
        "& .MuiAutocomplete-inputRoot": {
          flexWrap: "wrap",
          maxHeight: "120px",
          overflowY: "auto",
          paddingRight: "65px !important",
        },
        "& .MuiAutocomplete-endAdornment": {
          position: "absolute",
          right: "9px",
          top: "50%",
          transform: "translateY(-50%)",
        },
      }}
    />
  );
});

MultiSelectAutocomplete.displayName = "MultiSelectAutocomplete";

export default MultiSelectAutocomplete;
